import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin, User, Target, Trophy, Images, ArrowRight, Newspaper } from "lucide-react";
import { fmtDate, mediaUrl } from "../../lib/api";
import { StatusBadge } from "./Common";

export const NewsCard = ({ item }) => {
  const inner = (
    <>
      <div className="aspect-[16/10] overflow-hidden bg-slate-100">
        {item.image ? (
          <img src={mediaUrl(item.image)} alt={item.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="h-full w-full grid place-items-center text-slate-300"><Newspaper className="h-12 w-12" /></div>
        )}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
          <span className="font-semibold text-blue-700 uppercase tracking-wider">{item.category}</span>
          <span>•</span>
          <span data-testid="news-card-date">{fmtDate(item.date)}</span>
        </div>
        <h3 className="font-display text-lg font-semibold text-[#0B1F44] leading-snug">{item.title}</h3>
        <p className="mt-2 text-sm text-slate-600 line-clamp-3 flex-1">{item.description}</p>
        <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 group-hover:gap-3 transition-[gap]">Read more <ArrowRight className="h-4 w-4" /></span>
      </div>
    </>
  );
  const cls = "group card flex flex-col overflow-hidden";
  return item.external_link && !item.content ? (
    <a href={item.external_link} target="_blank" rel="noreferrer" className={cls} data-testid={`news-card-${item.id}`}>{inner}</a>
  ) : (
    <Link to={`/news/${item.id}`} className={cls} data-testid={`news-card-${item.id}`}>{inner}</Link>
  );
};

const Meta = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 text-sm">
    <Icon className="h-4 w-4 mt-0.5 text-blue-600 shrink-0" />
    <span className="text-slate-500 w-14 shrink-0">{label}</span>
    <span className="text-slate-800 font-medium">{value || "To be announced"}</span>
  </div>
);

export const timeRange = (e) => (e.start_time ? `${e.start_time}${e.end_time ? ` – ${e.end_time}` : ""}` : "");
export const dateRange = (e) => (e.end_date ? `${fmtDate(e.date)} – ${fmtDate(e.end_date)}` : fmtDate(e.date));

export const EventCard = ({ item }) => (
  <article className="card p-6 md:p-7 flex flex-col" data-testid={`event-card-${item.id}`}>
    <div className="flex items-center gap-2 mb-4">
      <span className="rounded-full bg-[#0B1F44] text-white px-2.5 py-0.5 text-xs font-semibold">{item.type}</span>
      <StatusBadge status={item.status} />
    </div>
    <h3 className="font-display text-xl font-semibold text-[#0B1F44]">
      <Link to={`/events/${item.id}`} className="hover:text-blue-700 transition-colors">{item.title}</Link>
    </h3>
    <p className="mt-2 text-sm text-slate-600 line-clamp-2">{item.description}</p>
    <div className="mt-5 space-y-2.5 border-t border-slate-100 pt-5 flex-1">
      <Meta icon={User} label="Trainer" value={item.organizer} />
      <Meta icon={Calendar} label="When" value={dateRange(item)} />
      <Meta icon={Clock} label="Time" value={timeRange(item)} />
      <Meta icon={MapPin} label="Venue" value={item.venue} />
      <Meta icon={Target} label="Level" value={item.level} />
    </div>
    <Link to={`/events/${item.id}`} className="mt-6 btn-outline text-sm self-start" data-testid={`event-view-details-${item.id}`}>View details <ArrowRight className="h-4 w-4" /></Link>
  </article>
);

export const AchievementCard = ({ item, onPhotos }) => (
  <article className="card p-6 md:p-7 flex flex-col" data-testid={`achievement-card-${item.id}`}>
    <div className="flex items-center justify-between mb-4">
      <span className="h-11 w-11 rounded-xl bg-amber-50 text-amber-600 grid place-items-center"><Trophy className="h-5 w-5" /></span>
      <span className="font-display text-2xl font-bold text-blue-700">{item.year}</span>
    </div>
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{item.category}</p>
    <h3 className="mt-1 font-display text-lg font-semibold text-[#0B1F44]">{item.title}</h3>
    <p className="mt-2 text-sm text-slate-600 flex-1">{item.description}</p>
    {item.venue && <p className="mt-3 text-sm text-slate-500 flex gap-2"><MapPin className="h-4 w-4 mt-0.5" />{item.venue}</p>}
    {item.photos?.length > 0 && (
      <button onClick={() => onPhotos(item.photos.map((u) => ({ url: u, media_type: "photo", title: item.title })))} className="mt-5 btn-outline text-sm self-start" data-testid={`achievement-view-photos-${item.id}`}>
        <Images className="h-4 w-4" /> View photos ({item.photos.length})
      </button>
    )}
  </article>
);
